/**
 * One region as a card, for the phone roster (BON-534).
 *
 * The design's mobile all-regions view stacks the roster into cards rather than
 * scrolling the table sideways: the name, then the director and the counts on
 * one quiet line, then the money and the ticket fill. A table at 390px puts the
 * figures off-screen, which is the one thing the roster exists to show.
 */
import { Link } from 'react-router-dom';

import { FillBar } from '../../primitives/FillBar';

import type { OverviewRegionRow } from './overview-types';
import { fillPercent, formatMoney } from './roster-columns';

export interface RegionRosterTileProps {
  row: OverviewRegionRow;
}

/** "Ada Lovelace · 16 venues · 64 workshops", skipping whatever is absent. */
function summarise(row: OverviewRegionRow): string {
  const venues = `${row.venueCount} ${row.venueCount === 1 ? 'venue' : 'venues'}`;
  const workshops = `${row.workshopCount} ${row.workshopCount === 1 ? 'workshop' : 'workshops'}`;
  return [row.directorName, venues, workshops].filter(Boolean).join(' · ');
}

export function RegionRosterTile({ row }: RegionRosterTileProps) {
  const percent = fillPercent(row.ticketsSold, row.ticketCapacity);

  return (
    <Link
      to={`/admin/overview/${row.path.join('/')}`}
      style={{
        display: 'block',
        padding: '14px 16px',
        borderRadius: 14,
        background: 'var(--nb-bg)',
        border: '1px solid var(--nb-border)',
        textDecoration: 'none',
        color: 'var(--nb-ink)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--nb-bark-dark)' }}>
          {row.name}
        </span>
        <span aria-hidden="true" style={{ color: 'var(--nb-faint)' }}>
          ›
        </span>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--nb-subtle)', marginTop: 3 }}>
        {summarise(row)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
        <span
          className="nb-tabular nb-mono"
          style={{ fontSize: 17, fontWeight: 700, color: 'var(--nb-bark-dark)' }}
        >
          {formatMoney(row.revenueCents)}
        </span>
        <div style={{ flex: 1 }} />
        <FillBar sold={row.ticketsSold} cap={row.ticketCapacity} width={90} />
        <span
          className="nb-tabular nb-mono"
          style={{ fontSize: 12, color: 'var(--nb-muted)', minWidth: 30, textAlign: 'right' }}
        >
          {percent}%
        </span>
      </div>
    </Link>
  );
}
